import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, Output, inject} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiStatus} from '@taiga-ui/kit';
import {
  WithdrawalStatus,
  WithdrawalStatusHistoryStatus,
} from '@gatewee/types/withdrawal';
import {StatusHistory, StatusHistoryAttachment} from '@gatewee/types/system';
import {GateweeCollapsibleDirective} from '../gatewee-collapsible/gatewee-collapsible.directive';
import {GateweeExpandChevronComponent} from '../gatewee-expand-chevron/gatewee-expand-chevron.component';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';

type MarkerTone = 'positive' | 'negative' | 'warning' | 'neutral' | 'info';

/**
 * TODO: Migrate to GateweeStatusHistoryComponent (gatewee-status-history)
 * with configurable titleKey / emptyKey / statusI18nPrefix / statusColorFn.
 */
@Component({
  selector: 'gatewee-withdrawal-status-history',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    GateweeIconComponent,
    GateweeExpandChevronComponent,
    GateweeCollapsibleDirective,
    TuiStatus,
  ],
  templateUrl: './gatewee-withdrawal-status-history.component.html',
  styleUrls: [
    './gatewee-withdrawal-status-history.component.css',
    '../styles/gatewee-collapsible-details.css',
  ],
})
export class GateweeWithdrawalStatusHistoryComponent {
  private readonly translate = inject(TranslateService);

  @Input() statusHistory: StatusHistory[] | null | undefined;

  @Output() downloadAttachment = new EventEmitter<StatusHistoryAttachment>();

  private readonly historyStatusSet = new Set<string>(
    Object.values(WithdrawalStatusHistoryStatus),
  );

  private readonly withdrawalStatusSet = new Set<string>(
    Object.values(WithdrawalStatus),
  );

  get items(): StatusHistory[] {
    return this.statusHistory ?? [];
  }

  displayStatus(status: string): string {
    const s = status || '';
    const historyKey = `enums.withdrawalStatusHistoryStatus.${s}`;
    let label = this.translate.instant(historyKey);
    if (label !== historyKey) {
      return label;
    }
    const withdrawalKey = `enums.withdrawalStatus.${s}`;
    label = this.translate.instant(withdrawalKey);
    if (label !== withdrawalKey) {
      return label;
    }
    return s;
  }

  /**
   * Hex colors for Taiga `tuiStatus` — aligned with recipient withdrawal / invoice views and table badges.
   */
  statusTuiColor(status: string): string {
    const s = (status || '').toLowerCase();

    if (this.historyStatusSet.has(s)) {
      if (s === WithdrawalStatusHistoryStatus.RETRY_SCHEDULED) {
        return '#FFD54F';
      }
      return '#FF7043';
    }

    if (['success', 'approved', 'paid', 'confirmed'].includes(s)) {
      return '#39B54A';
    }
    if (['failed', 'canceled', 'rejected'].includes(s)) {
      return '#FF7043';
    }
    if (
      ['pending', 'pending_approval', 'scheduled', 'processing'].includes(s)
    ) {
      return '#FFD54F';
    }
    if (['refunded'].includes(s)) {
      return '#79818C';
    }
    if (this.withdrawalStatusSet.has(s)) {
      return '#526ED3';
    }
    return '#526ED3';
  }

  markerIconWrapClass(status: string): Record<string, boolean> {
    const tone = this.markerTone(status);
    return {
      'gw-bg-status-positive': tone === 'positive',
      'gw-bg-status-negative': tone === 'negative',
      'gw-bg-status-warning': tone === 'warning',
      'gw-bg-status-neutral': tone === 'neutral',
      'gw-bg-status-info': tone === 'info',
    };
  }

  markerIcon(status: string): string {
    const tone = this.markerTone(status);
    if (tone === 'positive') {
      return '@tui.check-circle';
    }
    if (tone === 'negative') {
      return '@tui.x-circle';
    }
    if (tone === 'warning') {
      return '@tui.clock';
    }
    if (tone === 'info') {
      return '@tui.alert-circle';
    }
    return '@tui.minus-circle';
  }

  hasMetadata(metadata: Record<string, unknown>): boolean {
    return !!metadata && Object.keys(metadata).length > 0;
  }

  hasAttempt(history: StatusHistory): boolean {
    return this.effectiveAttempt(history) !== null;
  }

  attemptValue(history: StatusHistory): string {
    const n = this.effectiveAttempt(history);
    return n !== null ? String(n) : '';
  }

  /** Resolves attempt from top-level or metadata; API may send string or nest the field. */
  private effectiveAttempt(history: StatusHistory): number | null {
    const top = this.parseAttemptUnknown(history.attempt as unknown);
    if (top !== null) {
      return top;
    }
    const meta = history.metadata;
    if (!meta || typeof meta !== 'object') {
      return null;
    }
    const m = meta as Record<string, unknown>;
    return (
      this.parseAttemptUnknown(m['attempt']) ??
      this.parseAttemptUnknown(m['retryAttempt'])
    );
  }

  private parseAttemptUnknown(raw: unknown): number | null {
    if (raw === null || raw === undefined) {
      return null;
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      const t = raw.trim();
      if (!t.length) {
        return null;
      }
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  private markerTone(status: string): MarkerTone {
    const s = (status || '').toLowerCase();

    if (this.historyStatusSet.has(s)) {
      if (s === WithdrawalStatusHistoryStatus.RETRY_SCHEDULED) {
        return 'warning';
      }
      return 'negative';
    }

    if (['success'].includes(s)) {
      return 'positive';
    }
    if (['failed', 'canceled', 'rejected'].includes(s)) {
      return 'negative';
    }
    if (
      ['pending', 'pending_approval', 'processing', 'scheduled'].includes(s)
    ) {
      return 'warning';
    }
    if (['refunded'].includes(s)) {
      return 'neutral';
    }
    if (this.withdrawalStatusSet.has(s)) {
      return 'info';
    }
    return 'info';
  }
}
